import { useState, useEffect } from 'react';
import { Input, Button } from 'rsuite';
import Swal from 'sweetalert2';

interface ColorManagerProps {
    selectedColors: string[];
    onColorsChange: (colors: string[]) => void;
}

export default function ColorManager({ selectedColors, onColorsChange }: ColorManagerProps) {
    // 12 default colors
    const defaultColors = [
        'قرمز',
        'آبی',
        'سبز',
        'زرد',
        'سیاه',
        'سفید',
        'نارنجی',
        'بنفش',
        'طوسی',
        'قهوه‌ای',
        'خاکستری',
        'نقره‌ای'
    ];

    const [customColor, setCustomColor] = useState('');
    const [allColors, setAllColors] = useState<string[]>(defaultColors);

    // Initialize with default colors and any existing custom colors
    useEffect(() => {
        console.log('ColorManager - selectedColors prop:', selectedColors);
        if (selectedColors && selectedColors.length > 0) {
            // Add any colors from selectedColors that aren't in defaultColors
            const customColors = selectedColors.filter(
                color => !defaultColors.includes(color)
            );
            if (customColors.length > 0) {
                setAllColors([...defaultColors, ...customColors]);
            } else {
                // Reset to default if no custom colors
                setAllColors(defaultColors);
            }
        } else {
            // Reset to default if no selected colors
            setAllColors(defaultColors);
        }
    }, [selectedColors]);

    const handleColorToggle = (color: string) => {
        const isSelected = selectedColors.includes(color);
        let newSelectedColors: string[];

        if (isSelected) {
            // Remove color
            newSelectedColors = selectedColors.filter(c => c !== color);
        } else {
            // Add color
            newSelectedColors = [...selectedColors, color];
        }

        onColorsChange(newSelectedColors);
    };

    const handleAddCustomColor = () => {
        if (!customColor.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'خطا',
                text: 'لطفا نام رنگ را وارد کنید',
                confirmButtonText: 'باشه'
            });
            return;
        }

        if (allColors.includes(customColor.trim())) {
            Swal.fire({
                icon: 'warning',
                title: 'خطا',
                text: 'این رنگ قبلا اضافه شده است',
                confirmButtonText: 'باشه'
            });
            return;
        }

        // Add to all colors list
        const newAllColors = [...allColors, customColor.trim()];
        setAllColors(newAllColors);

        // Add to selected colors
        const newSelectedColors = [...selectedColors, customColor.trim()];
        onColorsChange(newSelectedColors);

        // Clear input
        setCustomColor('');
    };

    const handleRemoveCustomColor = (color: string) => {
        // Don't allow removing default colors
        if (defaultColors.includes(color)) {
            Swal.fire({
                icon: 'warning',
                title: 'خطا',
                text: 'نمی‌توان رنگ پیش‌فرض را حذف کرد',
                confirmButtonText: 'باشه'
            });
            return;
        }

        // Remove from all colors
        const newAllColors = allColors.filter(c => c !== color);
        setAllColors(newAllColors);

        // Remove from selected colors if selected
        const newSelectedColors = selectedColors.filter(c => c !== color);
        onColorsChange(newSelectedColors);
    };

    return (
        <div className="flex flex-col gap-4" dir="rtl">
            <div className="text-right text-lg font-bold mb-2">مدیریت رنگ‌ها</div>

            {/* Selected Colors Display */}
            {selectedColors.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-sm font-semibold text-blue-800 mb-2">
                        رنگ‌های انتخاب شده ({selectedColors.length}):
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {selectedColors.map((color, index) => (
                            <span
                                key={index}
                                className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-md text-sm font-medium border border-blue-300"
                            >
                                {color}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Default Colors Section */}
            <div>
                <div className="text-sm font-semibold text-gray-700 mb-3">
                    رنگ‌های پیش‌فرض:
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {defaultColors.map((color, index) => {
                        const isSelected = selectedColors.includes(color);
                        return (
                            <button
                                key={index}
                                type="button"
                                onClick={() => handleColorToggle(color)}
                                className={`
                                    px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200
                                    border-2 text-right
                                    ${isSelected
                                        ? 'bg-blue-500 text-white border-blue-600 shadow-md'
                                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                                    }
                                `}
                            >
                                {color}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Custom Colors Section */}
            <div className="border-t border-gray-200 pt-4">
                <div className="text-sm font-semibold text-gray-700 mb-3">
                    رنگ‌های سفارشی:
                </div>

                {/* Add Custom Color */}
                <div className="flex gap-2 mb-4">
                    <Input
                        placeholder="نام رنگ جدید را وارد کنید..."
                        value={customColor}
                        onChange={(value) => setCustomColor(value)}
                        onPressEnter={handleAddCustomColor}
                        className="flex-1"
                        dir="rtl"
                    />
                    <Button
                        onClick={handleAddCustomColor}
                        appearance="primary"
                        className="px-6"
                    >
                        افزودن رنگ
                    </Button>
                </div>

                {/* Custom Colors List */}
                {allColors.filter(color => !defaultColors.includes(color)).length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {allColors
                            .filter(color => !defaultColors.includes(color))
                            .map((color, index) => {
                                const isSelected = selectedColors.includes(color);
                                return (
                                    <div
                                        key={index}
                                        className="relative group"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => handleColorToggle(color)}
                                            className={`
                                                w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200
                                                border-2 text-right
                                                ${isSelected
                                                    ? 'bg-blue-500 text-white border-blue-600 shadow-md'
                                                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                                                }
                                            `}
                                        >
                                            {color}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveCustomColor(color)}
                                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                            title="حذف رنگ"
                                        >
                                            ×
                                        </button>
                                    </div>
                                );
                            })}
                    </div>
                )}

                {allColors.filter(color => !defaultColors.includes(color)).length === 0 && (
                    <div className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                        هنوز رنگ سفارشی اضافه نشده است
                    </div>
                )}
            </div>
        </div>
    );
}

